"use client"

import { useQuery } from "@tanstack/react-query"
import { getCampaignAction, listCampaignsAction } from "@/server-actions/campaigns"
import { isServerActionError } from "@/lib/with-query-handling"
import type { CampaignDetails, CampaignSummary } from "@/lib/campaigns/service"
import { useTranslations } from "next-intl"


export const campaignsQueryKey = ["campaigns"] as const

export const campaignQueryKey = (campaignId: string) => [...campaignsQueryKey, campaignId] as const
const CAMPAIGN_PAGE_REFRESH_MS = 60_000

export function useCampaignsQuery() {
  return useQuery<CampaignSummary[]>({
    queryKey: campaignsQueryKey,
    staleTime: CAMPAIGN_PAGE_REFRESH_MS,
    refetchInterval: CAMPAIGN_PAGE_REFRESH_MS,
    queryFn: async () => {
      const result = await listCampaignsAction({})
      if (isServerActionError(result)) throw new Error(result.errorMessage ?? result.error ?? "Failed to load campaigns")
      return result
    },
  })
}

export function useCampaignQuery(campaignId: string | null) {
  const tUi = useTranslations("Feedback.QueriesCampaigns")

  return useQuery<CampaignDetails>({
    queryKey: campaignId ? campaignQueryKey(campaignId) : [...campaignsQueryKey, "empty"],
    enabled: Boolean(campaignId),
    staleTime: CAMPAIGN_PAGE_REFRESH_MS,
    refetchInterval: campaignId ? CAMPAIGN_PAGE_REFRESH_MS : false,
    queryFn: async () => {
      if (!campaignId) throw new Error(tUi("campaignIDIsRequired"))
      const result = await getCampaignAction({ campaignId })
      if (isServerActionError(result)) throw new Error(result.errorMessage ?? result.error ?? "Failed to load campaign")
      return result
    },
  })
}
